import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function ContactPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — thanks!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="max-w-xl mx-auto px-4 py-8 pb-24">
      <Link to="/" data-testid="contact-back" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Jixxeo Apps
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#00F0FF]/15 text-[#00F0FF] flex items-center justify-center"><Mail className="w-6 h-6" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">Get in touch</div>
          <h1 className="font-black text-3xl leading-none">Contact us</h1>
        </div>
      </div>

      {sent ? (
        <div data-testid="contact-sent" className="rounded-3xl border border-[#39FF14]/40 bg-[#39FF14]/10 p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-[#39FF14] mx-auto mb-3" />
          <div className="font-black text-2xl">Message sent</div>
          <div className="text-sm text-white/70 mt-1">We usually reply within a couple of days.</div>
          <button data-testid="contact-again" onClick={() => { setSent(false); setForm({ ...form, subject: "", message: "" }); }}
            className="mt-5 inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full">
            Send another
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
          <p className="text-sm text-white/70 mb-2">Questions, bugs, partnership ideas, DMCA — drop us a line and we'll get back to you.</p>
          <Field label="Your name" required>
            <input data-testid="contact-name" required maxLength={80} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-xl px-3 py-2.5 text-sm outline-none" />
          </Field>
          <Field label="Email" required>
            <input data-testid="contact-email" type="email" required maxLength={200} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-xl px-3 py-2.5 text-sm outline-none" />
          </Field>
          <Field label="Subject" required>
            <input data-testid="contact-subject" required maxLength={140} value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-xl px-3 py-2.5 text-sm outline-none" />
          </Field>
          <Field label="Message" required>
            <textarea data-testid="contact-message" required maxLength={5000} rows={6} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-xl px-3 py-2.5 text-sm outline-none resize-y" />
          </Field>
          <button data-testid="contact-submit" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#00F0FF] text-black font-black text-sm uppercase tracking-widest px-5 py-3 rounded-full disabled:opacity-40 shadow-[0_0_20px_rgba(0,240,255,0.35)]">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send message</>}
          </button>
          <div className="text-[10px] text-white/40 text-center">By sending you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>.</div>
        </form>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}{required && <span className="text-[#FF00E5]"> *</span>}</div>
      {children}
    </label>
  );
}
